import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/config";

const isSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

/**
 * Hook that manages the browser Push subscription for the current staff user.
 * Exposes:
 *   - supported: boolean (browser has the necessary APIs)
 *   - permission: 'default' | 'granted' | 'denied'
 *   - subscribed: boolean (an active subscription exists for this browser)
 *   - loading: boolean
 *   - enable(), disable(), sendTest() — actions
 */
export function usePushNotifications() {
  const supported = isSupported();
  const [permission, setPermission] = useState(() => (supported ? Notification.permission : "unsupported"));
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg && (await reg.pushManager.getSubscription());
      setSubscribed(Boolean(sub));
      setPermission(Notification.permission);
    } catch (_) {
      setSubscribed(false);
    }
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  const enable = useCallback(async () => {
    if (!supported) throw new Error("Il tuo browser non supporta le notifiche push.");
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        throw new Error("Permesso notifiche negato. Abilitalo dalle impostazioni del browser.");
      }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const rawSub = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: rawSub.endpoint,
        keys: { p256dh: rawSub.keys.p256dh, auth: rawSub.keys.auth },
        ua: navigator.userAgent.slice(0, 380),
      });
      setSubscribed(true);
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg && (await reg.pushManager.getSubscription());
      // Unsubscribe locally FIRST so the browser sub is always cleaned up
      // regardless of network failures on the server side.
      if (sub) {
        try {
          await sub.unsubscribe();
        } catch (_) { /* ignore — best effort */ }
        try {
          await api.delete(`/push/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`);
        } catch (_) { /* server may have already pruned — ignore */ }
      } else {
        // No local subscription — clean up the server side just in case.
        try { await api.delete("/push/subscribe"); } catch (_) { /* ignore */ }
      }
      setSubscribed(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    const r = await api.post("/push/test", {});
    return r.data;
  }, []);

  return { supported, permission, subscribed, loading, enable, disable, sendTest, refresh };
}
